import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import copy from 'copy-to-clipboard';

import { Modal } from '#molecules';
import { Text } from '#atoms';

import { useToast } from '#providers/ToastProvider';
import { useUserInfo } from '#providers/UserInfo';
import { patchMyUrl } from '#api/my-url';
import { useMyUrl } from '#pages/MyPageMyUrl/hooks';

const ProfileMyUrlModal = ({
  setOnUrlModalOpen
}: {
  setOnUrlModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const [onChangeMyUrl, setOnChangeMyUrl] = useState(false);

  const { userInfo, setUserInfo } = useUserInfo();
  const { myUrl, updateMyUrl } = useMyUrl({
    currentMyUrl: userInfo?.myUrl ?? ''
  });
  const { showToast } = useToast();
  const { mutateAsync } = useMutation(patchMyUrl);

  const profileUrl = userInfo?.myUrl
    ? `https://fibud.kr/expert/${userInfo.myUrl}`
    : null;

  const handleCopy = () => {
    if (profileUrl) {
      copy(profileUrl);
      showToast({
        message: '링크가 복사되었습니다.',
        type: 'success'
      });
    }
  };

  const saveMyUrl = () => {
    if (!userInfo) return;

    if (myUrl.value.includes('.')) {
      showToast({ type: 'fail', message: 'url에는 .이 포함될 수 없습니다.' });
      return;
    }

    mutateAsync(myUrl.value, {
      onSuccess: () => {
        setUserInfo({ ...userInfo, myUrl: myUrl.value });
        showToast({ message: '내 프로필 링크를 수정하였습니다.' });
      },
      onError: () => {
        showToast({
          type: 'fail',
          message: '내 프로필 링크 수정에 실패하였습니다.'
        });
      }
    });
  };

  useEffect(() => {
    userInfo && userInfo.myUrl && updateMyUrl(userInfo.myUrl);
  }, [userInfo]);

  return (
    <Modal onClose={() => setOnUrlModalOpen(false)}>
      <div className="flex flex-col gap-40 w-full">
        <div className="flex flex-col gap-24">
          <p className="text-[#C9C7D1] text-[20px]">프로필 공유하기</p>
          <div className="flex flex-col gap-12">
            <div className="flex justify-between items-center">
              <p className="text-[#C9C7D1]">내 프로필 링크</p>
              <button
                type="button"
                onClick={handleCopy}
                className="text-[#9170F2] text-[14px]"
              >
                링크 복사
              </button>
            </div>
            <div className="px-16 py-8 rounded-8 bg-[#2E2B36] text-[#8E899F] flex items-center w-full">
              <p>fibud.kr/experts/</p>
              <input
                className="w-full bg-transparent text-[#8E899F]"
                value={myUrl.value}
                onChange={e => updateMyUrl(e.target.value)}
                disabled={!onChangeMyUrl}
              />
            </div>
            {!myUrl.isValid && (
              <Text textColor="red" textStyle="c2">
                url 형식에 맞는 문자열을 입력해주세요
              </Text>
            )}
          </div>
        </div>

        <div className="flex justify-between gap-12">
          {onChangeMyUrl ? (
            <ChangeUrlButtons
              setOnChangeMyUrl={setOnChangeMyUrl}
              onSaveMyUrl={saveMyUrl}
            />
          ) : (
            <DefaultButtons
              setOnChangeMyUrl={setOnChangeMyUrl}
              setOnUrlModalOpen={setOnUrlModalOpen}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

const DefaultButtons = ({
  setOnChangeMyUrl,
  setOnUrlModalOpen
}: {
  setOnChangeMyUrl: Dispatch<SetStateAction<boolean>>;
  setOnUrlModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <>
      <button
        type="button"
        onClick={() => setOnChangeMyUrl(true)}
        className="text-center bg-[#24222A] py-12 rounded-8 w-full text-[#BCB8C3]"
      >
        링크 변경
      </button>
      <button
        type="button"
        onClick={() => setOnUrlModalOpen(false)}
        className="text-center bg-[#7750E9] py-12 rounded-8 w-full text-[#EBE9F4]"
      >
        확인
      </button>
    </>
  );
};

const ChangeUrlButtons = ({
  setOnChangeMyUrl,
  onSaveMyUrl
}: {
  setOnChangeMyUrl: Dispatch<SetStateAction<boolean>>;
  onSaveMyUrl: () => void;
}) => {
  return (
    <>
      <button
        type="button"
        onClick={() => setOnChangeMyUrl(false)}
        className="text-center bg-[#24222A] py-12 rounded-8 w-full text-[#BCB8C3]"
      >
        취소
      </button>
      <button
        type="button"
        onClick={onSaveMyUrl}
        className="text-center bg-[#7750E9] py-12 rounded-8 w-full text-[#EBE9F4]"
      >
        변경 완료
      </button>
    </>
  );
};

export default ProfileMyUrlModal;
